import React, { Fragment, useMemo, useState } from "react";
import {
	useTable,
	useFlexLayout,
} from "react-table";
import "./ResourceTable.css";
import Tooltip from "./form-components/Tooltip.jsx";
import { DraggableVirtualTag } from "./VirtualTagsTable/DraggableVirtualTag.jsx";
import { StaticVirtualTagRow } from "./VirtualTagsTable/StaticVirtualTagRow.jsx";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
	arrayMove,
	SortableContext,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
	rectIntersection,
	DndContext,
	DragOverlay,
	KeyboardSensor,
	PointerSensor,
	MouseSensor,
	TouchSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";



function Table({ columns, data, setData, selectVirtualTag }) {
    const [activeId, setActiveId] = useState();
    const [placeholderIndex, setPlaceholderIndex] = useState(null);
    const items = useMemo(() => data?.map((row) => row.id), [data]);

	const defaultColumn = React.useMemo(
		() => ({
		  minWidth: 30,
		  width: 150,
		  maxWidth: 800,
		}),
		[]
	  )

	const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
		useTable(
			{
				columns,
				data,
				defaultColumn,
			},
			useFlexLayout
		);

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(MouseSensor),
		useSensor(TouchSensor),
		useSensor(KeyboardSensor)
	);

	function handleDragStart(event) {
		setActiveId(event.active.id);
	}

    function handleDragEnd(event) {
        const { active, over } = event;
        const oldIndex = items.indexOf(active.id);
        let newIndex = oldIndex;

        if (placeholderIndex !== null) {
            newIndex = placeholderIndex;
        } else if (over && active.id !== over.id) {
            newIndex = items.indexOf(over.id);
        }

        if (newIndex !== oldIndex && newIndex >= 0) {
            setData(arrayMove(data, oldIndex, newIndex));
        }

        setActiveId(null);
        setPlaceholderIndex(null);
    }

    function handleDragCancel() {
        setActiveId(null);
        setPlaceholderIndex(null);
    }

    function handleDragOver(event) {
        const { active, over } = event;
        if (!over) {
            setPlaceholderIndex(null);
            return;
        }

        const overIndex = items.indexOf(over.id);

        // Determine if cursor is in top or bottom half of the hovered row
        const overRect = over.rect;
        if (overRect && overRect.top !== undefined && overRect.height !== undefined) {
            const cursorY = event.activatorEvent && event.activatorEvent.clientY
                ? event.activatorEvent.clientY
                : (active.rect?.current?.translated?.top || 0) + (active.rect?.current?.translated?.height || 0) / 2;

            const overMid = overRect.top + overRect.height / 2;
            const insertIndex = cursorY < overMid ? overIndex : overIndex + 1;
            setPlaceholderIndex(insertIndex);
        } else {
            // Fallback: place before hovered item
            setPlaceholderIndex(overIndex);
        }
    }

	const selectedRow = useMemo(() => {
		if (!activeId) {
			return null;
		}
		const row = rows.find((row) => String(row.original.id) === String(activeId));
		prepareRow(row);
		return row;
	}, [activeId, rows, prepareRow]);

	return (
        <DndContext
			onDragStart={handleDragStart}
			onDragEnd={handleDragEnd}
			onDragCancel={handleDragCancel}
            onDragOver={handleDragOver}
			sensors={sensors}
			collisionDetection={rectIntersection}
			modifiers={[restrictToVerticalAxis]}
		>
			{(() => {
				const { key: tableKey, ...tableProps } = getTableProps();
				return (
					<div
						key={tableKey}
						{...tableProps}
						className="flex w-full cursor-default flex-auto flex-col"
					>
						<div className="py-2 text-text-2">
							{headerGroups.map((headerGroup) => {
								const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
								return (
									<div key={key} {...headerGroupProps} className="tr">
										{headerGroup.headers.map((column, colIndex) => {
										const { key, ...columnProps } = column.getHeaderProps();
										return (
											<div
												key={key}
												{...columnProps}
												className={`th flex items-center text-[11px] font-medium uppercase tracking-wide ${
													column.align === "center"
														? "justify-center text-center"
														: `text-left ${colIndex === 0 ? "pl-5" : "pl-2"}`
												}`}
											>
												{column.render("Header")}
											</div>
										);
									})}
									</div>
								);
							})}
						</div>

						{(() => {
							const { key: tableBodyKey, ...tableBodyProps } = getTableBodyProps();
							return (
                                <div key={tableBodyKey} {...tableBodyProps}>
                                    <SortableContext items={items} strategy={verticalListSortingStrategy}>
                                        {rows.map((row, index) => {
                                            prepareRow(row);
                                            const isLastRow = index === rows.length - 1;

                                            const elements = [];

                                            if (activeId && placeholderIndex === index) {
                                                elements.push(
                                                    <div key={`placeholder-${index}`} className="tr rounded-[11px] bg-[#EAEEF5] mb-[3px]" style={{ height: 44 }} />
                                                );
                                            }

                                            if (activeId !== row.original.id) {
                                                elements.push(
                                                    <DraggableVirtualTag key={row.original.id} row={row} index={index} isLastRow={isLastRow} selectVirtualTag={selectVirtualTag} />
                                                );
                                            }

                                            if (activeId && placeholderIndex === rows.length && index === rows.length - 1) {
                                                elements.push(
                                                    <div key={`placeholder-end`} className="tr rounded-[11px] bg-[#EAEEF5]" style={{ height: 44 }} />
                                                );
                                            }


                                            return (
                                                <Fragment key={`frag-${row.original.id}`}>{elements}</Fragment>
                                            );
                                        })}
                                    </SortableContext>
                                </div>
							);
						})()}
					</div>
				);
			})()}
			<DragOverlay>
				{activeId && (() => {
					const { key: overlayTableKey, ...overlayTableProps } = getTableProps();
					const { key: overlayBodyKey, ...overlayBodyProps } = getTableBodyProps();
					return (
						<div
							key={overlayTableKey}
							{...overlayTableProps}
							className="flex w-full cursor-default flex-auto flex-col"
						>
							<div key={overlayBodyKey} {...overlayBodyProps}>
								<StaticVirtualTagRow row={selectedRow} />
							</div>
						</div>
					);
				})()}
			</DragOverlay>
		</DndContext>
	);
}

const VirtualTagsTable = ({ data, setData, selectVirtualTag }) => {
	const formatDate = (dateString) => {
		if (!dateString) return "";
		const date = new Date(dateString);
		const day = String(date.getDate()).padStart(2, "0");
		const month = String(date.getMonth() + 1).padStart(2, "0");
		const year = date.getFullYear();
		return `${day}-${month}-${year}`;
	};

	const columnDefs = [
		{
			accessor: "name",
			Header: () => (
				<span data-tooltip-id="instantTooltip" data-tooltip-content="The name of the virtual tag">
					Virtual Tag
				</span>
			),
			Cell: (row) => <span className="font-medium text-text truncate">{row.value}</span>,
			flex: 1,
			minWidth: 200,
		},
        {
            accessor: "active",
            align: "center",
            Header: () => (
				<span data-tooltip-id="instantTooltip" data-tooltip-content="Whether the virtual tag is active or inactive">
					Active
				</span>
			),
            Cell: (row) => (
				<span
					className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
						row.value ? "bg-success-soft text-success" : "bg-danger-soft text-danger"
					}`}
				>
					{row.value ? "Active" : "Inactive"}
				</span>
			),
            width: 110,
            maxWidth: 120,
            minWidth: 100,
        },
        {
            accessor: "created_at",
            align: "center",
            Header: () => (
				<span data-tooltip-id="instantTooltip" data-tooltip-content="When the virtual tag was created">
					Created At
				</span>
			),
            Cell: (row) => <span className="text-text-2">{formatDate(row.value)}</span>,
            width: 150,
            minWidth: 140,
            maxWidth: 160,
        },
        {
            accessor: "updated_at",
            align: "center",
            Header: () => (
				<span data-tooltip-id="instantTooltip" data-tooltip-content="When the virtual tag was last updated">
					Updated At
				</span>
			),
            Cell: (row) => <span className="text-text-2">{formatDate(row.value)}</span>,
            width: 150,
            minWidth: 140,
            maxWidth: 160,
        },
	];

	if (data && data.length > 0) {
		return (
			<Fragment>
				<Tooltip />
				<Table className="flex-auto" columns={columnDefs} data={data} setData={setData} selectVirtualTag={selectVirtualTag}/>
			</Fragment>
		);
	} else {
		return (
			<Fragment>
				<div className="ml-10 font-semibold text-3xl text-text"></div>
			</Fragment>
		);
	}
};

export default VirtualTagsTable;
